import { useUserRole } from './useUserRole';
import type { UserPermissions } from '@/types';

/**
 * Hook to compute hierarchy visibility permissions based on user role.
 *
 * Permission Matrix:
 * - superadmin (internal HT): sees all levels
 * - admin (internal HT): sees all levels
 * - group_admin: sees groups and sites only
 * - user: sees sites only
 *
 * Note: This hook controls VISIBILITY of hierarchy levels in sidebar/filters.
 * For filter dropdown visibility (which dropdowns appear), use useFilterPermissions.
 */
export function useUserPermissions(): { permissions: UserPermissions; loading: boolean } {
  const { role, loading } = useUserRole();

  // Superadmin and Admin are internal HT roles - see everything
  const isInternal = role === 'superadmin' || role === 'admin';

  const permissions: UserPermissions = {
    // Internal roles (superadmin, admin) see all companies
    canSeeCompanies: isInternal,
    // Internal roles, company_admin, and group_admin see groups
    canSeeGroups: isInternal || role === 'company_admin' || role === 'group_admin',
    // Everyone can see sites
    canSeeSites: true,
  };

  return { permissions, loading };
}
